import Phaser from 'phaser';
import { CampManager } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { RunManager } from '../systems/RunManager';
import { Survivor } from '../systems/CampManager';
import { TeamMember } from '../systems/RunManager';
import { InventoryManager } from '../systems/InventoryManager';
import { ITEM_DEFINITIONS, SLOT_LABELS, CONSUMABLE_DEFINITIONS } from '../data/items';
import { ItemInstance, PersonalSlot, PERSONAL_SLOTS } from '../types/Item.types';
import { CLASS_DEFINITIONS } from '../constants';
import { SfxManager } from '../utils/SfxManager';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { ExpeditionManager } from '../systems/ExpeditionManager';
import {
  loadAllPresets, saveAllPresets, snapshotCurrent, applyPreset,
  EquipmentPreset,
} from '../systems/EquipmentPresets';
import { savePrepSnapshot, clearPrepSnapshot } from '../systems/PrepSnapshot';
import { RARITY_COLORS, formatBonuses, getItemColorNum } from '../ui/InventoryUI';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { TutorialOverlay } from '../ui/TutorialOverlay';
import { FONT_BODY } from '../utils/Typography';
import { ensureTouchTarget } from '../ui/MobileTouchUI';
import { LifetimeStats } from '../systems/LifetimeStats';

// === SCÈNE DE PRÉPARATION DE RUN ===
// Utilise InventoryManager comme source de vérité unique.

const PREP_TUTO_KEY = 'deadtide_prep_tuto_done';

const HEADER_H = 95;
const MAX_TEAM = 3;

export class RunPrepScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private camp!: CampManager;
  private meta!: MetaManager;
  private run!: RunManager;
  private inv!: InventoryManager;
  private sfx = new SfxManager();
  /** Équipe sélectionnée : le capitaine (null) + survivants */
  private selectedIds: (string | null)[] = [null]; // null = capitaine, toujours slot 1
  /** Onglet personnage actif pour l'équipement (index dans selectedIds) */
  private activeEquipTab = 0;
  /** Bug fix v0.38.13 — anti-ghost-click mobile : un tap déclenche un mousedown
   * synthétique 0-300ms après le touchend. Si on a redessiné l'UI entre les deux,
   * le mousedown frappe sur les NOUVEAUX boutons à la même position pixel.
   * `isProcessing` bloque tous les handlers pendant 300ms après une action.
   * Identique au pattern RunEndScene. */
  private isProcessing = false;
  /** Liste des éléments de popup (équipement/sac) à détruire si l'UI est redessinée
   * pendant qu'une popup est ouverte (ex. ghost-click sur tab pendant popup). */
  private popupElements: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'RunPrepScene' });
  }

  init(data: { camp: CampManager; meta: MetaManager; presetRun?: RunManager }): void {
    this.camp = data.camp;
    this.meta = data.meta;
    this.meta.startRun();
    // Sprint 82 v3 — Si presetRun fourni (combat secret Captain Jack), on l'utilise
    // pour conserver la map dédiée au lieu d'en générer une nouvelle.
    this.run = data.presetRun ?? new RunManager(this.meta, this.camp);
    this.inv = this.run.getInventory();
    this.selectedIds = [null]; // capitaine toujours sélectionné
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
    this.showPrepTutorial();
  }

  /** Tutoriel affiché à la toute première préparation de run */
  private showPrepTutorial(): void {
    let done = false;
    try { done = !!localStorage.getItem(PREP_TUTO_KEY); } catch { /* */ }
    if (done) return;

    const tuto = new TutorialOverlay(this, [
      {
        text: '⚓ Préparez votre équipe !\n\nAvant de partir, équipez vos personnages\navec les items du Coffre.\nUn bon équipement peut faire la différence !',
        x: GAME_CX, y: 280,
      },
      {
        text: '🗡️ Slots d\'équipement (5 par perso) :\nArme • Tête • Corps • Bottes • Bague\n\nCliquez sur un slot pour choisir\nun item depuis votre coffre.',
        x: GAME_CX, y: 300,
      },
      {
        text: '💊 Sac partagé (max 3 consommables) :\nPotion de soins, Ration, etc.\nIls s\'utilisent pendant le combat\net peuvent vous sauver la vie !',
        x: GAME_CX, y: 300,
      },
      {
        text: '👥 Équipe jusqu\'à 3 membres :\n⚔️ Guerrier — Résistant, attaque mêlée\n🏹 Tireur — Portée longue, LOS requis\n💊 Médecin — Soigne, fragile en mêlée\n\nCochoz les survivants à emmener.',
        x: GAME_CX, y: 350,
      },
    ], () => {
      try { localStorage.setItem(PREP_TUTO_KEY, '1'); } catch { /* */ }
    });
    tuto.start();
  }

  /** Demande un redraw au prochain tick + active la garde anti-ghost-click 300ms.
   * À utiliser DANS un pointerdown handler — ne JAMAIS appeler drawUI()
   * directement depuis un handler car Phaser est en pleine itération sur
   * la liste des objets interactifs et la destruction (removeAll) laisse
   * le plugin input dans un état où les events suivants ciblent au mauvais
   * endroit (cf. bug "clics aux endroits plus visibles" en multi-persos). */
  private redrawSoon(): void {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.time.delayedCall(0, () => this.drawUI());
    this.time.delayedCall(300, () => { this.isProcessing = false; });
  }

  private closePopup(): void {
    this.popupElements.forEach(e => e.destroy());
    this.popupElements = [];
  }

  private drawUI(): void {
    // Cleanup explicite : popup ouverte + listeners scene.input du panel
    // sortant. Sans destroy() du panel, ScrollablePanel.setupInput() ajoute
    // 4 listeners (wheel, pointerdown, pointermove, pointerup) à chaque
    // redraw → fuite progressive qui peut interférer avec d'autres scènes.
    this.closePopup();
    this.panel?.destroy();
    this.children.removeAll(true);

    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);

    this.add.text(GAME_CX, 15, '⚓ PRÉPARATION', {
      fontSize: '20px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    this.add.text(GAME_CX, 38, 'Équipe-toi depuis le coffre avant de partir !', {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    // Stats du perso actif dans l'onglet équipement
    const activeId = this.selectedIds[this.activeEquipTab];
    const activeClass = activeId ? (this.camp.getSurvivors().find(s => s.id === activeId)?.class ?? 'warrior') : 'warrior';
    const classDef = CLASS_DEFINITIONS[activeClass];
    const memberId = activeId ?? 'captain';
    const stats = this.inv.computeStats(memberId, { ...classDef.stats });
    this.add.text(GAME_CX, 56, `${classDef.emoji} ${classDef.name}  HP:${stats.maxHp}  ATK:${stats.attack}  DEF:${stats.defense}  PA:${stats.pa}  PM:${stats.pm}`, {
      fontSize: '11px', color: '#3498db', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    // Boutons header
    const backBtn = this.add.text(15, 10, '← Camp', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => {
      this.unequipAllToStorage();
      // Tout est revenu au coffre — le snapshot n'a plus de raison d'être,
      // sinon le prochain boot ferait des doublons.
      clearPrepSnapshot();
      startSceneWithFade(this, 'CampScene', { camp: this.camp, meta: this.meta });
    });
    backBtn.on('pointerover', () => backBtn.setColor('#ff6b6b'));
    backBtn.on('pointerout', () => backBtn.setColor('#e74c3c'));

    const goBtn = this.add.text(GAME_W - 170, 10, '⚔️ Partir !', {
      fontSize: '12px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 8, y: 4 },
    }).setDepth(11);
    goBtn.setInteractive({ useHandCursor: true });
    goBtn.on('pointerdown', () => this.buildTeamAndStart());
    goBtn.on('pointerover', () => goBtn.setColor('#27ae60'));
    goBtn.on('pointerout', () => goBtn.setColor('#2ecc71'));

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);

    let y = 10;

    // Positions responsive (adapté mobile 450px / desktop 800px)
    const rowW = Math.min(540, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const rx = Math.round(GAME_CX + rowW / 2 - 4);

    // Filtrer survivants vivants disponibles (hors expédition)
    const expeditionMgr = new ExpeditionManager();
    const allAliveSurvivors = this.camp.getAliveSurvivors()
      .filter(s => !expeditionMgr.isSurvivorOnExpedition(s.id));

    // Nettoyer selectedIds : retirer toute référence à un survivant qui n'est
    // plus disponible (mort en run, parti en expé, supprimé du camp). Sans
    // cela, selectedIds peut contenir des fantômes qui faussent teamSize.
    const aliveIds = new Set(allAliveSurvivors.map(s => s.id));
    this.selectedIds = this.selectedIds.filter(id => id === null || aliveIds.has(id));
    // Borner activeEquipTab dans selectedIds (sinon onglets cassés après retrait)
    if (this.activeEquipTab >= this.selectedIds.length) {
      this.activeEquipTab = Math.max(0, this.selectedIds.length - 1);
    }

    const selectedSurvivors = allAliveSurvivors.filter(s => this.selectedIds.includes(s.id));
    const availableSurvivors = allAliveSurvivors.filter(s => !this.selectedIds.includes(s.id));

    // === ÉQUIPE SÉLECTIONNÉE ===
    const teamSize = this.selectedIds.length;
    this.panel.add(this.add.text(lx, y, `--- ⚓ Équipe (${teamSize}/${MAX_TEAM}) ---`, {
      fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    y += 22;

    // Capitaine (toujours sélectionné, non retirable)
    y = this.drawTeamSlot(null, y, true);
    y += 6;

    // Survivants déjà dans l'équipe (avec [Retirer])
    for (const s of selectedSurvivors) {
      y = this.drawTeamSlot(s, y, true);
      y += 6;
    }

    y += 10;

    // === SURVIVANTS DISPONIBLES ===
    if (availableSurvivors.length > 0) {
      const teamFull = this.selectedIds.length >= MAX_TEAM;
      const suffix = teamFull ? ' — équipe complète' : '';
      this.panel.add(this.add.text(lx, y, `--- 👥 Survivants disponibles (${availableSurvivors.length})${suffix} ---`, {
        fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
      }));
      y += 22;
      for (const s of availableSurvivors) {
        y = this.drawTeamSlot(s, y, false);
        y += 6;
      }
    } else if (allAliveSurvivors.length === 0) {
      this.panel.add(this.add.text(GAME_CX, y, 'Recrutez des survivants pour agrandir votre équipe !', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
      y += 20;
    }

    y += 10;

    // === PRESETS D'ÉQUIPEMENT ===
    y = this.drawPresetsRow(y, lx, rowW);
    y += 10;

    // === ONGLETS PERSONNAGES POUR L'ÉQUIPEMENT ===
    if (this.selectedIds.length > 1) {
      this.panel.add(this.add.text(Math.round(GAME_CX - Math.min(540, GAME_W - 10) / 2 + 8), y, '--- Équipement par personnage ---', {
        fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
      }));
      y += 22;

      const _mobTab = GAME_W < 500;
      const tabAreaW = GAME_W - 30;
      const tabWidth = Math.floor(tabAreaW / this.selectedIds.length);
      for (let t = 0; t < this.selectedIds.length; t++) {
        const tid = this.selectedIds[t];
        const tcls = tid ? (this.camp.getSurvivors().find(s => s.id === tid)?.class ?? 'warrior') : 'warrior';
        const tdef = CLASS_DEFINITIONS[tcls];
        const tname = tid ? (this.camp.getSurvivors().find(s => s.id === tid)?.name ?? tdef.name) : 'Capitaine';
        const isActive = t === this.activeEquipTab;
        const tabX = 15 + t * tabWidth;

        // Sur mobile : tronque le nom pour rentrer dans tabWidth (~140 px)
        const displayName = _mobTab && tname.length > 7 ? tname.slice(0, 6) + '…' : tname;
        const tabBtn = this.add.text(tabX, y, `${tdef.emoji} ${displayName}`, {
          fontSize: _mobTab ? '10px' : '11px', color: isActive ? '#f1c40f' : '#7f8c8d',
          fontFamily: FONT_BODY, fontStyle: isActive ? 'bold' : 'normal',
          backgroundColor: isActive ? '#1a2a40' : '#151525',
          padding: { x: _mobTab ? 4 : 8, y: 6 },
        });
        if (isActive) {
          this.panel.add(this.add.rectangle(tabX + tabBtn.width / 2, y + 28, tabBtn.width, 2, 0xf1c40f));
        }
        if (!isActive) {
          tabBtn.setInteractive({ useHandCursor: true });
          tabBtn.on('pointerdown', () => { this.activeEquipTab = t; this.redrawSoon(); });
        }
        this.panel.add(tabBtn);
      }
      y += 35;
    } else {
      this.panel.add(this.add.text(lx, y, '--- Équipement ---', {
        fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
      }));
      y += 22;
    }

    // Slots équipés du personnage actif (sans sac — le sac est partagé)
    const activeInvId = this.selectedIds[this.activeEquipTab] ?? 'captain';
    for (const slot of PERSONAL_SLOTS) {
      y = this.drawEquipSlot(slot, y, activeInvId);
      y += 6;
    }

    // === SAC À DOS PARTAGÉ (toute l'équipe) ===
    y += 10;
    this.panel.add(this.add.text(lx, y, '--- 🎒 Sac à dos (équipe) ---', {
      fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    y += 22;

    const sharedBag = this.inv.getBackpack();
    const bagBg = this.add.rectangle(GAME_CX, y + 17, rowW, 36, 0x1e2d4a).setStrokeStyle(1, 0x2a3a5c);
    this.panel.add(bagBg);

    // La ligne sac à dos est cliquable pour choisir dans le coffre
    bagBg.setInteractive({ useHandCursor: true });
    bagBg.on('pointerdown', () => this.showBackpackPopup());
    bagBg.on('pointerover', () => bagBg.setFillStyle(0x253d5a));
    bagBg.on('pointerout', () => bagBg.setFillStyle(0x1e2d4a));

    if (sharedBag) {
      const bagDef = ITEM_DEFINITIONS[sharedBag.definitionId];
      const bagColor = RARITY_COLORS[bagDef.rarity] ?? '#ffffff';
      this.panel.add(this.add.text(lx, y + 3, `🎒 ${bagDef.name}`, {
        fontSize: '11px', color: bagColor, fontFamily: FONT_BODY,
      }));
      this.panel.add(this.add.text(lx, y + 18, `+${bagDef.bonuses.capacity ?? 0} places  (${this.inv.getTransportCapacity()} total)`, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }));
      const removeBagBtn = this.add.text(rx, y + 8, '[Retirer]', {
        fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
        backgroundColor: '#2c2a1a', padding: { x: 3, y: 2 },
      }).setOrigin(1, 0);
      removeBagBtn.setInteractive({ useHandCursor: true });
      removeBagBtn.on('pointerdown', (_p: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation(); // empêche d'ouvrir la popup en même temps
        this.sfx.select();
        const removed = this.inv.unequipBackpack();
        if (removed) this.camp.storeItem({ uid: removed.uid, definitionId: removed.definitionId });
        this.redrawSoon();
      });
      ensureTouchTarget(removeBagBtn);
      this.panel.add(removeBagBtn);
    } else {
      this.panel.add(this.add.text(lx, y + 8, '🎒 → cliquer pour équiper un sac', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }));
    }
    y += 40;

    // Coffre (exclure items équipés par les persos + sac partagé)
    y += 10;
    const allEquippedUids = new Set<string>();
    for (const sid of this.selectedIds) {
      const mid = sid ?? 'captain';
      const equip = this.inv.getEquipment(mid);
      for (const s of PERSONAL_SLOTS) {
        if (equip[s]) allEquippedUids.add(equip[s]!.uid);
      }
    }
    if (sharedBag) allEquippedUids.add(sharedBag.uid);
    const storage = this.camp.getStorage().filter(i => !allEquippedUids.has(i.uid));
    this.panel.add(this.add.text(lx, y, `--- Coffre (${storage.length}/${this.camp.getStorageCapacity()}) ---`, {
      fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    y += 22;

    if (storage.length === 0) {
      this.panel.add(this.add.text(GAME_CX, y + 10, '(coffre vide)', {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
      y += 30;
    } else {
      for (const item of storage) {
        y = this.drawStorageItem(item, y);
        y += 6;
      }
    }

    // Consommables stockés au camp
    const consumables = this.camp.getConsumables();
    if (consumables.length > 0) {
      y += 10;
      this.panel.add(this.add.text(lx, y, `--- Consommables camp (${consumables.length}) — Équipe: ${this.inv.getConsumables().length}/${this.inv.getMaxConsumables()} ---`, {
        fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
        wordWrap: { width: rowW },
      }));
      y += 22;
      for (const c of consumables) {
        const def = CONSUMABLE_DEFINITIONS[c.definitionId];
        if (!def) continue;
        const bg = this.add.rectangle(GAME_CX, y + 10, rowW, 26, 0x162038)
          .setStrokeStyle(1, 0xbdc3c7);
        this.panel.add(bg);
        this.panel.add(this.add.text(lx, y + 3, `${def.emoji} ${def.name} — ${def.description}`, {
          fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY,
          wordWrap: { width: rowW * 0.75 },
        }));

        const consFull = this.inv.getConsumables().length >= this.inv.getMaxConsumables();
        const btn = this.add.text(rx, y + 2, consFull ? 'Max' : '[Prendre]', {
          fontSize: '10px', color: consFull ? '#7f8c8d' : '#2ecc71', fontFamily: FONT_BODY,
          backgroundColor: consFull ? '#151525' : '#1a2a1a', padding: { x: 3, y: 2 },
        }).setOrigin(1, 0);
        if (!consFull) {
          btn.setInteractive({ useHandCursor: true });
          btn.on('pointerdown', () => {
            this.sfx.select();
            const withdrawn = this.camp.withdrawConsumable(c.uid);
            if (withdrawn) {
              this.inv.addConsumable(withdrawn.definitionId);
            }
            this.redrawSoon();
          });
          ensureTouchTarget(btn);
        }
        this.panel.add(btn);
        y += 26;
        y += 6;
      }
    }

    y += 40;
    this.panel.setContentHeight(y);

    // === GARDE-FOU ANTI-PERTE ITEMS (v0.38.17) ===
    // Persiste l'état actuel de l'inventaire pour qu'un refresh navigateur ne
    // fasse pas disparaître les items pris dans le coffre. Au prochain boot,
    // BootScene appellera restoreFromPrepSnapshot() pour les remettre.
    savePrepSnapshot(this.inv);
  }

  /** Affiche les 3 slots de presets d'équipement avec boutons Sauver/Charger.
   *  Permet au joueur de stocker un loadout complet en localStorage et de le
   *  réappliquer en 1 clic au début d'un run. Best-effort : si un item du preset
   *  n'est plus dans le coffre (vendu, perdu en run, recyclé), il est ignoré. */
  private drawPresetsRow(y: number, lx: number, rowW: number): number {
    this.panel.add(this.add.text(lx, y, '--- 💼 Presets d\'équipement ---', {
      fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    y += 20;
    this.panel.add(this.add.text(lx, y, 'Sauve un loadout pour l\'appliquer en 1 clic au prochain run.', {
      fontSize: '9px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }));
    y += 16;

    const presets = loadAllPresets();
    const slotW = Math.floor(rowW / 3) - 4;

    for (let i = 0; i < presets.length; i++) {
      const preset = presets[i];
      const slotX = Math.round(GAME_CX - rowW / 2 + 4 + i * (slotW + 4));
      const bg = this.add.rectangle(slotX + slotW / 2, y + 22, slotW, 44, preset.isEmpty ? 0x161825 : 0x1a2a40)
        .setStrokeStyle(1, preset.isEmpty ? 0x33384a : 0x2a4a6c);
      this.panel.add(bg);

      // Label : "Preset N" + état
      this.panel.add(this.add.text(slotX + 6, y + 3, `Preset ${i + 1}`, {
        fontSize: '11px', color: preset.isEmpty ? '#7f8c8d' : '#f1c40f',
        fontFamily: FONT_BODY, fontStyle: 'bold',
      }));

      const stateLabel = preset.isEmpty
        ? 'vide'
        : `${preset.members.length} pers · ${this.summarizePreset(preset)}`;
      this.panel.add(this.add.text(slotX + 6, y + 18, stateLabel, {
        fontSize: '9px', color: '#7f8c8d', fontFamily: FONT_BODY,
        wordWrap: { width: slotW - 50 },
      }));

      // [💾] Sauver en haut à droite
      const saveBtn = this.add.text(slotX + slotW - 6, y + 4, '💾', {
        fontSize: '12px', color: '#2ecc71', fontFamily: FONT_BODY,
        backgroundColor: '#1a2a1a', padding: { x: 3, y: 2 },
      }).setOrigin(1, 0);
      saveBtn.setInteractive({ useHandCursor: true });
      saveBtn.on('pointerdown', (_p: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();
        this.sfx.select();
        const memberIds = this.selectedIds.map(id => id ?? 'captain');
        const newPreset = snapshotCurrent(this.inv, memberIds, i);
        const all = loadAllPresets();
        all[i] = newPreset;
        saveAllPresets(all);
        this.flashMessage(`Preset ${i + 1} sauvegardé !`, '#2ecc71');
        this.redrawSoon();
      });
      this.panel.add(saveBtn);

      // [📥] Charger en bas à droite — désactivé si preset vide
      const loadBtn = this.add.text(slotX + slotW - 6, y + 24, '📥', {
        fontSize: '12px',
        color: preset.isEmpty ? '#3a4252' : '#3498db',
        fontFamily: FONT_BODY,
        backgroundColor: preset.isEmpty ? '#0e0e15' : '#1a2540',
        padding: { x: 3, y: 2 },
      }).setOrigin(1, 0);
      if (!preset.isEmpty) {
        loadBtn.setInteractive({ useHandCursor: true });
        loadBtn.on('pointerdown', (_p: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
          event.stopPropagation();
          this.sfx.select();
          const memberIds = this.selectedIds.map(id => id ?? 'captain');
          const result = applyPreset(this.inv, this.camp, preset, memberIds);
          if (result.aborted) {
            // v0.38.17 — Coffre trop petit pour accueillir l'équipement actuel
            this.flashMessage('Coffre plein — déséquipe avant d\'appliquer le preset', '#e74c3c');
          } else {
            const summary = result.missing > 0 || result.consumablesMissing > 0
              ? `Preset ${i + 1} chargé (${result.equipped} équipés, ${result.missing} manquants)`
              : `Preset ${i + 1} chargé !`;
            this.flashMessage(summary, result.missing > 0 ? '#f39c12' : '#2ecc71');
          }
          this.redrawSoon();
        });
      }
      this.panel.add(loadBtn);
    }

    return y + 50;
  }

  /** Description courte d'un preset pour l'UI : compte items + sac + cons. */
  private summarizePreset(preset: EquipmentPreset): string {
    let items = 0;
    for (const m of preset.members) {
      for (const k of PERSONAL_SLOTS) if (m[k]) items++;
    }
    const parts: string[] = [];
    if (items > 0) parts.push(`${items} obj`);
    if (preset.backpack) parts.push('🎒');
    if (preset.consumables.length > 0) parts.push(`${preset.consumables.length} cons.`);
    return parts.length > 0 ? parts.join(' · ') : 'rien';
  }

  /** Toast bref centré pour confirmer une action (sauver/charger preset). */
  private flashMessage(text: string, color: string): void {
    const flash = this.add.text(GAME_CX, GAME_CY, text, {
      fontSize: '13px', color, fontFamily: FONT_BODY, fontStyle: 'bold',
      backgroundColor: '#0a0a1e', padding: { x: 14, y: 8 },
    }).setOrigin(0.5).setDepth(300);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      y: GAME_CY - 30,
      duration: 1400,
      ease: 'Power2',
      onComplete: () => flash.destroy(),
    });
  }

  private drawEquipSlot(slot: PersonalSlot, y: number, memberId: string = 'captain'): number {
    const rW = Math.min(540, GAME_W - 10);
    const elx = Math.round(GAME_CX - rW / 2 + 8);
    const erx = Math.round(GAME_CX + rW / 2 - 4);
    const equip = this.inv.getEquipment(memberId);
    const item = equip[slot];
    const label = SLOT_LABELS[slot] ?? slot;

    const rowH = item ? 44 : 34;
    const bg = this.add.rectangle(GAME_CX, y + rowH / 2, rW, rowH, 0x1e2d4a).setStrokeStyle(1, 0x2a3a5c);
    this.panel.add(bg);

    // Toute la ligne ouvre la popup de sélection d'équipement
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', () => this.showEquipPopup(slot, memberId));
    bg.on('pointerover', () => bg.setFillStyle(0x253d5a));
    bg.on('pointerout', () => bg.setFillStyle(0x1e2d4a));

    if (item) {
      const def = ITEM_DEFINITIONS[item.definitionId];
      const color = RARITY_COLORS[def.rarity] ?? '#ffffff';
      this.panel.add(this.add.text(elx, y + 3, `★ ${label}  ${def.name}`, {
        fontSize: '11px', color, fontFamily: FONT_BODY, fontStyle: 'bold',
      }));

      const bonuses = formatBonuses(def.bonuses);
      if (bonuses) {
        this.panel.add(this.add.text(elx, y + 18, bonuses, {
          fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
        }));
      }

      const btn = this.add.text(erx, y + 8, '[Retirer]', {
        fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
        backgroundColor: '#2c2a1a', padding: { x: 3, y: 2 },
      }).setOrigin(1, 0);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', (_p: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation(); // empêche d'ouvrir la popup en même temps
        this.sfx.select();
        // Retirer l'item du slot et le remettre au coffre
        const removed = this.inv.unequipFromCharacter(memberId, slot);
        if (removed) {
          this.camp.storeItem({ uid: removed.uid, definitionId: removed.definitionId });
        }
        this.redrawSoon();
      });
      ensureTouchTarget(btn);
      this.panel.add(btn);
    } else {
      this.panel.add(this.add.text(elx, y + 4, `${label}  → cliquer pour équiper`, {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }));
    }

    return y + rowH;
  }

  private drawTeamSlot(survivor: Survivor | null, y: number, isInTeam: boolean): number {
    const rW = Math.min(540, GAME_W - 10);
    const tlx = Math.round(GAME_CX - rW / 2 + 8);
    const trx = Math.round(GAME_CX + rW / 2 - 4);
    const classDef = CLASS_DEFINITIONS[survivor?.class ?? 'warrior'];
    const name = survivor ? survivor.name : 'Le Capitaine';
    const isCaptain = survivor === null;

    const bgColor = isInTeam ? 0x1a3a2a : 0x1e2d4a;
    const borderColor = isInTeam ? 0x2ecc71 : 0x2a3a5c;

    const bg = this.add.rectangle(GAME_CX, y + 18, rW, 42, bgColor)
      .setStrokeStyle(isInTeam ? 2 : 1, borderColor);
    this.panel.add(bg);

    const slotNum = isInTeam ? `[${isCaptain ? 1 : this.selectedIds.indexOf(survivor!.id) + 1}]` : '   ';
    this.panel.add(this.add.text(tlx, y + 4, `${slotNum} ${classDef.emoji} ${name} — ${classDef.name}`, {
      fontSize: '11px', color: isInTeam ? '#2ecc71' : '#bdc3c7', fontFamily: FONT_BODY,
      fontStyle: isInTeam ? 'bold' : 'normal',
    }));
    // Passif de classe
    this.panel.add(this.add.text(tlx + 20, y + 20, `Passif: ${classDef.passive}`, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }));

    if (isCaptain) {
      this.panel.add(this.add.text(trx, y + 5, '✓', {
        fontSize: '11px', color: '#2ecc71', fontFamily: FONT_BODY,
      }).setOrigin(1, 0));
    } else if (isInTeam) {
      const btn = this.add.text(trx, y + 3, '[Retirer]', {
        fontSize: '10px', color: '#e74c3c', fontFamily: FONT_BODY,
        backgroundColor: '#2c1a1a', padding: { x: 3, y: 2 },
      }).setOrigin(1, 0);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.sfx.select();
        this.selectedIds = this.selectedIds.filter(id => id !== survivor!.id);
        // Remettre l'équipement de ce membre au coffre
        const mid = survivor!.id;
        for (const slot of PERSONAL_SLOTS) {
          const removed = this.inv.unequipFromCharacter(mid, slot);
          if (removed) this.camp.storeItem({ uid: removed.uid, definitionId: removed.definitionId });
        }
        this.redrawSoon();
      });
      ensureTouchTarget(btn);
      this.panel.add(btn);
    } else if (this.selectedIds.length < MAX_TEAM) {
      const btn = this.add.text(trx, y + 3, '[Ajouter]', {
        fontSize: '10px', color: '#f39c12', fontFamily: FONT_BODY,
        backgroundColor: '#2c2a1a', padding: { x: 3, y: 2 },
      }).setOrigin(1, 0);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.sfx.select();
        this.selectedIds.push(survivor!.id);
        this.redrawSoon();
      });
      ensureTouchTarget(btn);
      this.panel.add(btn);
    } else {
      this.panel.add(this.add.text(trx, y + 5, 'Plein', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(1, 0));
    }

    return y + 42;
  }

  private drawStorageItem(item: ItemInstance, y: number): number {
    const def = ITEM_DEFINITIONS[item.definitionId];
    if (!def) return y;
    const rW = Math.min(540, GAME_W - 10);
    const slx = Math.round(GAME_CX - rW / 2 + 8);
    const srx = Math.round(GAME_CX + rW / 2 - 4);
    const color = RARITY_COLORS[def.rarity] ?? '#ffffff';
    const slotLabel = SLOT_LABELS[def.slot] ?? def.slot;

    const bg = this.add.rectangle(GAME_CX, y + 10, rW, 26, 0x162038)
      .setStrokeStyle(1, getItemColorNum(item.definitionId));
    this.panel.add(bg);

    this.panel.add(this.add.text(slx, y + 3, `${slotLabel}  ${def.name} — ${def.description}`, {
      fontSize: '10px', color, fontFamily: FONT_BODY,
      wordWrap: { width: rW * 0.7 },
    }));

    const btn = this.add.text(srx, y + 2, '[Équiper]', {
      fontSize: '10px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 3, y: 2 },
    }).setOrigin(1, 0);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => {
      // Guard anti-double tap
      const stillInStorage = this.camp.getStorage().some(i => i.uid === item.uid);
      if (!stillInStorage) return;
      this.sfx.select();
      const withdrawn = this.camp.withdrawItem(item.uid);
      if (!withdrawn) return;

      if (def.slot === 'backpack') {
        // Sac à dos → slot partagé
        const oldBag = this.inv.equipBackpack(withdrawn);
        if (oldBag) {
          this.camp.storeItem({ uid: oldBag.uid, definitionId: oldBag.definitionId });
        }
      } else {
        // Équipement individuel → slot du personnage actif
        const activeInvId = this.selectedIds[this.activeEquipTab] ?? 'captain';
        const slot = def.slot as PersonalSlot;
        const old = this.inv.equipItemOnCharacter(withdrawn, activeInvId, slot);
        if (old) {
          this.camp.storeItem({ uid: old.uid, definitionId: old.definitionId });
        }
      }
      this.redrawSoon();
    });
    this.panel.add(btn);

    return y + 26;
  }

  /** Popup : liste les items du coffre équipables dans ce slot, sur le personnage actif */
  private showEquipPopup(slot: PersonalSlot, memberId: string): void {
    // Exclure les items déjà équipés par n'importe quel membre
    const allEquippedUids = new Set<string>();
    for (const sid of this.selectedIds) {
      const mid = sid ?? 'captain';
      const equip = this.inv.getEquipment(mid);
      for (const s of PERSONAL_SLOTS) {
        if (equip[s]) allEquippedUids.add(equip[s]!.uid);
      }
    }
    const sharedBag = this.inv.getBackpack();
    if (sharedBag) allEquippedUids.add(sharedBag.uid);

    const matching = this.camp.getStorage()
      .filter(i => !allEquippedUids.has(i.uid))
      .filter(i => {
        const def = ITEM_DEFINITIONS[i.definitionId];
        return def && def.slot === slot;
      });

    const label = SLOT_LABELS[slot] ?? slot;

    if (matching.length === 0) {
      const flash = this.add.text(GAME_CX, GAME_CY, `Aucun item pour "${label}" dans le coffre`, {
        fontSize: '12px', color: '#e74c3c', fontFamily: FONT_BODY,
        backgroundColor: '#2c1a1a', padding: { x: 12, y: 6 },
      }).setOrigin(0.5).setDepth(200);
      this.time.delayedCall(1500, () => flash.destroy());
      return;
    }

    // Toute popup en cours = obsolète : drawUI() la fermera de toute façon,
    // mais on close avant pour éviter d'avoir 2 popups empilées si l'utilisateur
    // re-clique avant que le tick suivant arrive.
    this.closePopup();
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.6).setDepth(200).setInteractive();
    this.popupElements.push(overlay);

    const title = this.add.text(GAME_CX, 200, `Équiper : ${label}`, {
      fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(201);
    this.popupElements.push(title);

    let popY = 235;
    for (const item of matching) {
      const def = ITEM_DEFINITIONS[item.definitionId];
      const color = RARITY_COLORS[def.rarity] ?? '#ffffff';
      const bonuses = formatBonuses(def.bonuses);
      const colorNum = getItemColorNum(item.definitionId);

      const itemBg = this.add.rectangle(GAME_CX, popY + 16, Math.min(400, GAME_W - 20), 34, 0x1e2d4a)
        .setStrokeStyle(1, colorNum).setDepth(201);
      const txt = this.add.text(GAME_CX - Math.min(190, GAME_W / 2 - 15), popY + 4, `${def.name}`, {
        fontSize: '11px', color, fontFamily: FONT_BODY,
      }).setDepth(202);
      const stat = this.add.text(GAME_CX - Math.min(190, GAME_W / 2 - 15), popY + 18, bonuses || '(aucun bonus)', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setDepth(202);

      itemBg.setInteractive({ useHandCursor: true });
      itemBg.on('pointerdown', () => {
        // Guard anti-double tap
        if (!this.camp.getStorage().some(i => i.uid === item.uid)) return;
        this.sfx.select();
        const withdrawn = this.camp.withdrawItem(item.uid);
        if (!withdrawn) return;
        // L'éventuel ancien item retourne au coffre
        const old = this.inv.equipItemOnCharacter(withdrawn, memberId, slot);
        if (old) this.camp.storeItem({ uid: old.uid, definitionId: old.definitionId });
        this.redrawSoon(); // drawUI() détruira la popup via closePopup()
      });
      itemBg.on('pointerover', () => itemBg.setStrokeStyle(2, 0xf1c40f));
      itemBg.on('pointerout', () => itemBg.setStrokeStyle(1, colorNum));

      this.popupElements.push(itemBg, txt, stat);
      popY += 38;
    }

    const cancelBtn = this.add.text(GAME_CX, popY + 15, '[ Annuler ]', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(201);
    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => this.closePopup());
    this.popupElements.push(cancelBtn);
  }

  /** Popup : liste les sacs disponibles dans le coffre */
  private showBackpackPopup(): void {
    const allEquippedUids = new Set<string>();
    for (const sid of this.selectedIds) {
      const mid = sid ?? 'captain';
      const equip = this.inv.getEquipment(mid);
      for (const s of PERSONAL_SLOTS) {
        if (equip[s]) allEquippedUids.add(equip[s]!.uid);
      }
    }
    const sharedBag = this.inv.getBackpack();
    if (sharedBag) allEquippedUids.add(sharedBag.uid);

    const matching = this.camp.getStorage()
      .filter(i => !allEquippedUids.has(i.uid))
      .filter(i => {
        const def = ITEM_DEFINITIONS[i.definitionId];
        return def && def.slot === 'backpack';
      });

    if (matching.length === 0) {
      const flash = this.add.text(GAME_CX, GAME_CY, 'Aucun sac disponible dans le coffre', {
        fontSize: '12px', color: '#e74c3c', fontFamily: FONT_BODY,
        backgroundColor: '#2c1a1a', padding: { x: 12, y: 6 },
      }).setOrigin(0.5).setDepth(200);
      this.time.delayedCall(1500, () => flash.destroy());
      return;
    }

    this.closePopup();
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.6).setDepth(200).setInteractive();
    this.popupElements.push(overlay);

    const title = this.add.text(GAME_CX, 200, 'Équiper : 🎒 Sac à dos', {
      fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(201);
    this.popupElements.push(title);

    let popY = 235;
    for (const item of matching) {
      const def = ITEM_DEFINITIONS[item.definitionId];
      const color = RARITY_COLORS[def.rarity] ?? '#ffffff';
      const bonuses = formatBonuses(def.bonuses);
      const colorNum = getItemColorNum(item.definitionId);

      const itemBg = this.add.rectangle(GAME_CX, popY + 16, Math.min(400, GAME_W - 20), 34, 0x1e2d4a)
        .setStrokeStyle(1, colorNum).setDepth(201);
      const txt = this.add.text(GAME_CX - Math.min(190, GAME_W / 2 - 15), popY + 4, `🎒 ${def.name}`, {
        fontSize: '11px', color, fontFamily: FONT_BODY,
      }).setDepth(202);
      const stat = this.add.text(GAME_CX - Math.min(190, GAME_W / 2 - 15), popY + 18, bonuses || '(aucun bonus)', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setDepth(202);

      itemBg.setInteractive({ useHandCursor: true });
      itemBg.on('pointerdown', () => {
        if (!this.camp.getStorage().some(i => i.uid === item.uid)) return;
        this.sfx.select();
        const withdrawn = this.camp.withdrawItem(item.uid);
        if (!withdrawn) return;
        // L'éventuel ancien sac retourne au coffre
        const oldBag = this.inv.equipBackpack(withdrawn);
        if (oldBag) this.camp.storeItem({ uid: oldBag.uid, definitionId: oldBag.definitionId });
        this.redrawSoon();
      });
      itemBg.on('pointerover', () => itemBg.setStrokeStyle(2, 0xf1c40f));
      itemBg.on('pointerout', () => itemBg.setStrokeStyle(1, colorNum));

      this.popupElements.push(itemBg, txt, stat);
      popY += 38;
    }

    const cancelBtn = this.add.text(GAME_CX, popY + 15, '[ Annuler ]', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(201);
    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => this.closePopup());
    this.popupElements.push(cancelBtn);
  }

  /** Construit l'équipe TeamMember[] et lance le run */
  private buildTeamAndStart(): void {
    const team: TeamMember[] = [];

    for (const id of this.selectedIds) {
      const memberId = id ?? 'captain';

      if (id === null) {
        // Capitaine
        const classDef = CLASS_DEFINITIONS['warrior'];
        const computed = this.inv.computeStats('captain', { ...classDef.stats });
        team.push({
          id: 'captain',
          name: 'Le Capitaine',
          class: 'warrior',
          survivorRef: null,
          hp: computed.maxHp,
          maxHp: computed.maxHp,
          alive: true,
        });
      } else {
        // Survivant recruté
        const survivor = this.camp.getSurvivors().find(s => s.id === id);
        if (!survivor || !survivor.alive) continue;
        const classDef = CLASS_DEFINITIONS[survivor.class];
        const computed = this.inv.computeStats(memberId, { ...classDef.stats });
        team.push({
          id: survivor.id,
          name: survivor.name,
          class: survivor.class,
          survivorRef: survivor,
          hp: computed.maxHp,
          maxHp: computed.maxHp,
          alive: true,
        });
      }
    }

    this.run.setTeam(team);
    if (team.length > 0) {
      this.run.setSelectedSurvivor(team[0].survivorRef);
    }
    // Stats lifetime — comptabiliser chaque classe emmenée en run.
    // Appelé ici (et pas dans setTeam()) car setTeam est aussi invoqué au
    // chargement d'une save mid-run ; on ne veut compter qu'au démarrage réel.
    for (const member of team) LifetimeStats.recordClassPlayed(member.class);
    // Le run démarre : on persiste l'état (équipe + inventaire) AVANT de
    // basculer le snapshot de prépa, sinon une fenêtre de quelques ms entre
    // clearPrepSnapshot et le premier saveRunState (déclenché au choix d'un
    // nœud) laisserait l'inventaire en mémoire seule. Avec saveRunState ici,
    // un refresh juste après le départ retombera sur le bouton "Reprendre".
    this.run.saveRunState();
    clearPrepSnapshot();
    startSceneWithFade(this, 'MapScene', { run: this.run });
  }

  shutdown(): void {
    this.closePopup();
    this.panel?.destroy();
  }

  private unequipAllToStorage(): void {
    // Déséquiper TOUS les membres de l'équipe.
    // Garde-fou v0.38.17 : si le coffre est plein, on re-équipe pour ne pas
    // perdre l'item. L'utilisateur reste sur la prépa avec l'item équipé,
    // le snapshot le préserve pour le prochain boot.
    for (const id of this.selectedIds) {
      const memberId = id ?? 'captain';
      for (const slot of PERSONAL_SLOTS) {
        const removed = this.inv.unequipFromCharacter(memberId, slot);
        if (removed && !this.camp.storeItem({ uid: removed.uid, definitionId: removed.definitionId })) {
          this.inv.equipItemOnCharacter(removed, memberId, slot);
        }
      }
    }
    // Déséquiper le sac partagé
    const bag = this.inv.unequipBackpack();
    if (bag && !this.camp.storeItem({ uid: bag.uid, definitionId: bag.definitionId })) {
      this.inv.equipBackpack(bag);
    }
  }
}
